import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useMoodboard } from '../contexts/MoodboardContext';
import { moodboardService } from '../lib/moodboardService';
import {
  MoodboardSelection,
  RoomType,
  RoomStyle
} from '../types/moodboard';
import { AllGenerationsViewer } from '../components/AllGenerationsViewer';
import { GenerationLoadingOverlay, LOADING_SEQUENCES } from '../components/GenerationLoadingOverlay';
import { useGenerationCosts } from '../hooks/useGenerationCosts';

export const Moodboard: React.FC = () => {
  const { user, checkTokenBalance, refreshTokenBalance, updateUserTokens, isUpdatingTokens } = useAuth();
  const { categories, styles: roomStyles, sizes: roomSizes, loading: moodboardLoading } = useMoodboard();
  const { costs } = useGenerationCosts();
  const resultSectionRef = useRef<HTMLDivElement>(null);
  
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resultImages, setResultImages] = useState<string[]>([]);
  const [lastProcessingId, setLastProcessingId] = useState<string>('');

  const [roomTypes, setRoomTypes] = useState<RoomType[]>([]);
  const [selection, setSelection] = useState<MoodboardSelection>({
    category: null,
    roomType: null,
    roomStyles: [], // Changed from single roomStyle to empty array
    roomSize: null,
    prompt: '' // Initialize prompt as empty string
  });

  // Get dynamic token cost for moodboard generation - cost per style
  const baseCostPerStyle = costs.moodboard || 6; // Fallback to 6 if not loaded yet
  const currentTokenBalance = useMemo(() => user?.tokens || 0, [user?.tokens]);
  
  // Calculate total cost based on number of selected styles
  const TOKENS_REQUIRED = useMemo(() => {
    return selection.roomStyles.length * baseCostPerStyle;
  }, [selection.roomStyles.length, baseCostPerStyle]);

  useEffect(() => {
    if (selection.category) {
      loadRoomTypes(selection.category.id);
      setSelection(prev => ({ ...prev, roomType: null, roomStyles: [], roomSize: null })); // Keep prompt when changing category
    }
  }, [selection.category]);

  const loadRoomTypes = async (categoryId: string) => {
    try {
      const roomTypesData = await moodboardService.getRoomTypes(categoryId);
      setRoomTypes(roomTypesData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load room types');
    }
  };

  const handleStyleToggle = (style: RoomStyle) => {
    setSelection(prev => {
      const currentStyles = prev.roomStyles;
      const isSelected = currentStyles.some(s => s.id === style.id);
      
      if (isSelected) {
        // Remove style from selection
        return {
          ...prev,
          roomStyles: currentStyles.filter(s => s.id !== style.id)
        };
      } else {
        // Add style to selection
        return {
          ...prev,
          roomStyles: [...currentStyles, style]
        };
      }
    });
  };

  const handleSubmit = async () => {
    if (!user || !isSelectionComplete()) return;

    setLoading(true);
    setError(null);
    setSuccess(false);
    setResultImages([]);

    const processingId = `moodboard-${Date.now()}`;
    setLastProcessingId(processingId);

    try {
      // Use refreshTokenBalance to get the most up-to-date balance before spending
      await refreshTokenBalance(user);
      const updatedBalance = user?.tokens || 0;
      if (updatedBalance < TOKENS_REQUIRED) {
        setError(`Not enough tokens. You need ${TOKENS_REQUIRED}.`);
        setLoading(false);
        return;
      }
      
      const webhookUrl = process.env.REACT_APP_N8N_WEBHOOK_URL;
      if (!webhookUrl) throw new Error('Webhook URL not configured.');

      await moodboardService.createGeneration(user.id, {
        processing_id: processingId,
        category_id: selection.category!.id,
        room_type_id: selection.roomType!.id,
        room_style_id: selection.roomStyles[0]?.id || '', // Use first style for database compatibility
        room_size_id: selection.roomSize!.id,
        status: 'pending'
      });
      
      const payload = {
        user_id: user.id,
        processing_id: processingId,
        category: selection.category!.name,
        room_type: selection.roomType!.name,
        room_style: selection.roomStyles.map(s => s.name).join(', '), // Concatenate multiple styles
        room_size: selection.roomSize!.name,
        prompt: selection.prompt || '', // Include prompt in payload
      };

      if (process.env.NODE_ENV === 'development') {
        console.log('Sending payload to webhook:', payload);
      }

      const response = await fetch(webhookUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (process.env.NODE_ENV === 'development') {
        console.log('Received response from webhook:', response);
      }

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Webhook failed: ${response.statusText}. Response: ${errorText}`);
      }

      const result = await response.json();
      if (process.env.NODE_ENV === 'development') {
        console.log('Parsed JSON from webhook response:', result);
      }
      
      // Parse the response using the new service function
      const imageUrls = moodboardService.parseN8NMoodboardResponse(result);

      if (imageUrls.length > 0) {
        setResultImages(imageUrls);
        // Store all images (handles both single and multiple images)
        await moodboardService.markGenerationCompletedWithMultipleImages(processingId, imageUrls);
        updateUserTokens(currentTokenBalance - TOKENS_REQUIRED);
        setSuccess(true);
      } else {
        setError("AI operation finished but no image was returned.");
        moodboardService.markGenerationFailed(processingId, "No image returned from webhook.");
      }
    } catch (err) {
      console.error('An error occurred during moodboard submission:', err);
      const errorMessage = err instanceof Error ? err.message : 'An unknown error occurred.';
      setError(errorMessage);
      moodboardService.markGenerationFailed(processingId, errorMessage).catch(e => console.error(e));
    } finally {
      setLoading(false);
      checkTokenBalance(user);
    }
  };

  const isSelectionComplete = () => {
    return selection.category && selection.roomType && selection.roomStyles.length > 0 && selection.roomSize;
  };
  
  // Scroll to result section when result images are available
  useEffect(() => {
    if (resultImages.length > 0 && resultSectionRef.current) {
      resultSectionRef.current.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'start' 
      });
    }
  }, [resultImages]);

  if (moodboardLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading moodboard options...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Create Your Moodboard</h1>
          <p className="text-gray-600">Select your preferences to generate a custom design moodboard</p>
          <div className="mt-4 flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-4">
            <div className="inline-flex items-center bg-blue-100 text-blue-800 px-4 py-2 rounded-full text-sm font-medium">
              💰 Available: {currentTokenBalance} tokens | Required: {TOKENS_REQUIRED} ({selection.roomStyles.length} style{selection.roomStyles.length !== 1 ? 's' : ''} × {baseCostPerStyle} tokens)
            </div>
            <button onClick={() => refreshTokenBalance(user)} disabled={isUpdatingTokens} className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded text-xs font-medium">
              {isUpdatingTokens ? 'Refreshing...' : '🔄 Refresh'}
            </button>
          </div>
        </div>

        {error && <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md"><p className="text-red-700">{error}</p></div>}
        {success && <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-md"><p className="text-green-700">🎉 Moodboard generated successfully!</p></div>}
        
        {resultImages.length > 0 && (
          <div ref={resultSectionRef} className="mb-8 bg-white rounded-lg shadow-sm p-6">
            <h3 className="text-lg font-semibold mb-4">🎉 Results ({resultImages.length} images)</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              {resultImages.map((imageUrl, index) => (
                <div key={index} className="relative group">
                  <img 
                    src={imageUrl} 
                    alt={`Moodboard Result ${index + 1}`} 
                    className="w-full h-auto rounded-lg shadow-md group-hover:shadow-xl transition-shadow duration-300"
                  />
                  <div className="absolute top-2 left-2 bg-black bg-opacity-70 text-white px-2 py-1 rounded text-xs">
                    {index + 1}
                  </div>
                </div>
              ))}
            </div>
            <div className="flex flex-wrap gap-3 justify-center">
              {resultImages.map((imageUrl, index) => (
                <a 
                  key={index}
                  href={imageUrl} 
                  download={`${lastProcessingId}-${index + 1}.webp`} 
                  className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md text-sm transition-colors duration-200"
                >
                  Download Image {index + 1}
                </a>
              ))}
            </div>
          </div>
        )}

        <div className="bg-white rounded-lg shadow-sm p-6 space-y-8">
          {/* Step 1: Room Category */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">1. Room Category</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {categories.map((category) => (
                <button
                  key={category.id}
                  onClick={() => setSelection(prev => ({ ...prev, category }))}
                  className={`p-4 border rounded-lg text-left transition-all ${
                    selection.category?.id === category.id
                      ? 'border-blue-600 bg-blue-50 text-blue-900'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="font-medium">{category.name}</div>
                  <div className="text-sm text-gray-500 mt-1">{category.description}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Step 2: Room Type */}
          {selection.category && (
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">2. Room Type</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {roomTypes.map((roomType: RoomType) => (
                  <button
                    key={roomType.id}
                    onClick={() => setSelection(prev => ({ ...prev, roomType }))}
                    className={`p-4 border rounded-lg text-left transition-all ${
                      selection.roomType?.id === roomType.id
                        ? 'border-blue-600 bg-blue-50 text-blue-900'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="font-medium">{roomType.name}</div>
                    <div className="text-sm text-gray-500 mt-1">{roomType.description}</div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Step 3: Room Style */}
          {selection.roomType && (
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">3. Design Style (Select Multiple)</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {roomStyles.map((style) => {
                  const isSelected = selection.roomStyles.some(s => s.id === style.id);
                  return (
                    <button
                      key={style.id}
                      onClick={() => handleStyleToggle(style)}
                      className={`p-4 border rounded-lg text-left transition-all relative ${
                        isSelected
                          ? 'border-blue-600 bg-blue-50 text-blue-900'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="font-medium">{style.name}</div>
                          <div className="text-sm text-gray-500 mt-1">{style.description}</div>
                        </div>
                        <div className={`ml-2 w-5 h-5 rounded border-2 flex items-center justify-center ${
                          isSelected 
                            ? 'bg-blue-600 border-blue-600' 
                            : 'border-gray-300'
                        }`}>
                          {isSelected && (
                            <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          )}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
              {selection.roomStyles.length > 0 && (
                <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                  <p className="text-sm text-blue-800">
                    <strong>Selected styles:</strong> {selection.roomStyles.map(s => s.name).join(', ')}
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Step 4: Room Size */}
          {selection.roomStyles.length > 0 && (
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">4. Room Size</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {roomSizes.map((size) => (
                  <button
                    key={size.id}
                    onClick={() => setSelection(prev => ({ ...prev, roomSize: size }))}
                    className={`p-4 border rounded-lg text-left transition-all ${
                      selection.roomSize?.id === size.id
                        ? 'border-blue-600 bg-blue-50 text-blue-900'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="font-medium">{size.name}</div>
                    <div className="text-sm text-gray-500 mt-1">{size.description}</div>
                    {size.square_feet_min && size.square_feet_max && (
                      <div className="text-xs text-gray-400 mt-1">
                        {size.square_feet_min}-{size.square_feet_max} sq ft
                      </div>
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Step 5: Optional Prompt */}
          {selection.roomSize && (
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">5. Additional Details (Optional)</h3>
              <div className="space-y-4">
                <div>
                  <label htmlFor="prompt" className="block text-sm font-medium text-gray-700 mb-2">
                    Describe specific elements, colors, or details you'd like to see
                  </label>
                  <textarea
                    id="prompt"
                    rows={3}
                    value={selection.prompt || ''}
                    onChange={(e) => setSelection(prev => ({ ...prev, prompt: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="e.g., 'Add warm lighting, earth tones, plants, cozy reading nook with window view...'"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Optional: Provide additional context to help the AI generate exactly what you're envisioning
                  </p>
                </div>
                {selection.prompt && selection.prompt.length > 0 && (
                  <div className="p-3 bg-green-50 rounded-lg">
                    <p className="text-sm text-green-800">
                      <strong>Your prompt:</strong> {selection.prompt}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Submit Button */}
          {isSelectionComplete() && (
            <div className="border-t pt-6">
              <div className="flex flex-col sm:flex-row justify-between items-center space-y-4 sm:space-y-0">
                <div className="text-sm text-center sm:text-left text-gray-600">
                  Selected: {selection.category?.name} → {selection.roomType?.name} → {selection.roomStyles.map(s => s.name).join(', ')} → {selection.roomSize?.name}
                  {selection.prompt && <span className="block mt-1 text-gray-500">Custom details: {selection.prompt}</span>}
                </div>
                <button
                  onClick={handleSubmit}
                  disabled={loading || currentTokenBalance < TOKENS_REQUIRED}
                  className="bg-blue-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Generating...' : `Create Moodboard (${TOKENS_REQUIRED} tokens - ${selection.roomStyles.length} style${selection.roomStyles.length !== 1 ? 's' : ''})`}
                </button>
              </div>
            </div>
          )}
        </div>

        
        <AllGenerationsViewer  />
        
        
      </div>
      
      <GenerationLoadingOverlay
        isVisible={loading}
        loadingSteps={LOADING_SEQUENCES.MOODBOARD}
      />
    </div>
  );
}; 